import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useFormik } from 'formik';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Paginator } from 'primereact/paginator';
import MlxBadge from '../../../../../../components/common/MlxBadge/MlxBadge';
import MlxBlockUI from '../../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../../../../../components/common/MlxButton/MlxButton';
import MlxDialog from '../../../../../../components/common/MlxDialog/MlxDialog';
import MlxSearch from '../../../../../../components/common/MlxSearch/MlxSearch';
import { MENU_IMPORT_BATCH } from '../../../../../../constants/queryKeys';
import {
    MENU_IMPORT_CLOSE,
    MENU_IMPORT_ADDED,
    MENU_IMPORT_ADDING_DESC,
    MENU_IMPORT_ADDING_TITLE,
    MENU_IMPORT_DISCARD_ADDED_MSG,
    MENU_IMPORT_ERROR_GENERIC,
    MENU_IMPORT_ERROR_TITLE,
    MENU_IMPORT_ERROR_UNAVAILABLE,
    MENU_IMPORT_FINISH,
    MENU_IMPORT_FINISH_MSG,
    MENU_IMPORT_FINISH_TITLE,
    MENU_IMPORT_RETRY,
    MENU_IMPORT_REVIEW_CANCEL_EDIT,
    MENU_IMPORT_REVIEW_COL_DESCRIPTION,
    MENU_IMPORT_REVIEW_COL_NAME,
    MENU_IMPORT_REVIEW_COL_PRICE,
    MENU_IMPORT_REVIEW_MARK_READY,
    MENU_IMPORT_REVIEW_MARK_READY_ARIA,
    MENU_IMPORT_REVIEW_BACK_TO_REVIEW_ARIA,
    MENU_IMPORT_REVIEW_EDIT_ARIA,
    MENU_IMPORT_REVIEW_DELETE_ARIA,
    MENU_IMPORT_REVIEW_NO_DESCRIPTION,
    MENU_IMPORT_REVIEW_SELECTED_COUNT,
    MENU_IMPORT_REVIEW_SELECT_ALL_READY,
    MENU_IMPORT_REVIEW_REMOVE_COUNT,
    MENU_IMPORT_REVIEW_BULK_ACTIONS_ARIA,
    MENU_IMPORT_REVIEW_FILTER_ARIA,
    MENU_IMPORT_REVIEW_ITEM_DELETED,
    MENU_IMPORT_REVIEW_ITEMS_DELETED,
    MENU_IMPORT_REVIEW_UNDO,
    MENU_IMPORT_REVIEW_SHOWING,
    MENU_IMPORT_REVIEW_SHOWING_NONE,
    MENU_IMPORT_REVIEW_FOOTER_HINT,
    MENU_IMPORT_REVIEW_FOOTER_HINT_PENDING,
    MENU_IMPORT_ADD_N_ITEM,
    MENU_IMPORT_ADD_N_ITEMS,
    MENU_IMPORT_ADD_TO_MENU_LOWER,
    MENU_IMPORT_REVIEW_CONFLICT_MSG,
    MENU_IMPORT_REVIEW_CONFLICT_TITLE,
    MENU_IMPORT_REVIEW_DISCARD,
    MENU_IMPORT_REVIEW_DISCARD_MSG,
    MENU_IMPORT_REVIEW_DISCARD_TITLE,
    MENU_IMPORT_REVIEW_EMPTY,
    MENU_IMPORT_REVIEW_ERROR_DESCRIPTION,
    MENU_IMPORT_REVIEW_ERROR_NAME,
    MENU_IMPORT_REVIEW_ERROR_PRICE,
    MENU_IMPORT_REVIEW_NEEDS_REVIEW,
    MENU_IMPORT_REVIEW_NO_MATCH,
    MENU_IMPORT_REVIEW_READY,
    MENU_IMPORT_REVIEW_REMOVE_MANY,
    MENU_IMPORT_REVIEW_REMOVE_ONE,
    MENU_IMPORT_REVIEW_CLEAR,
    MENU_IMPORT_REVIEW_CLEAR_ARIA,
    MENU_IMPORT_REVIEW_REMOVE_TITLE,
    MENU_IMPORT_REVIEW_SAVE_ITEM,
    MENU_IMPORT_REVIEW_SAVE_REFUSED,
    MENU_IMPORT_REVIEW_SEARCH,
    MENU_IMPORT_REVIEW_SELECT_ITEM,
    MENU_IMPORT_REVIEW_SELECT_ALL,
    MENU_IMPORT_REVIEW_REMOVE_ARIA,
    MENU_IMPORT_REVIEW_FILES,
    MENU_IMPORT_REVIEW_SOURCE_TITLE,
    MENU_IMPORT_REVIEW_TAB_ALL,
    STATUS,
} from '../../../../../../constants/strings';
import { defaultPaginationEvent } from '../../../../../../helpers/commonHelper';
import { JTranslation } from '../../../../../../helpers/jTranslationHelper';
import { useTranslation } from '../../../../../../hooks/useTranslation';
import logger from '../../../../../../utils/logger';
import {
    _DraftTree,
    _MenuImportBatch,
    _MenuImportBatchResponse,
    _ReviewItemForm,
    _ReviewRow,
    _ReviewTab,
    applySelection,
    classifyMenuImportError,
    confirmItems,
    convertRowToReviewItemForm,
    editItem,
    filterReviewRows,
    flattenDraft,
    formatFileSize,
    getImportFileKind,
    initialReviewItemForm,
    keepServerFields,
    markForReview,
    publishBatch,
    removeItems,
    reviewItemSchema,
    updateDraft,
} from '../../../../../../helpers/menuImportHelper';
import { usePendingRemoval } from '../hooks/usePendingRemoval';
import ImportMessageDialog, { _ImportMessage } from './ImportMessageDialog';

type _ReviewStepProps = {
    venueId: string;
    batch: _MenuImportBatch;
    busy?: boolean;
    // Called once the admin has confirmed "Discard and start new"; the page cancels (or, once items were added, finishes) the batch.
    // A page that reports a failed cancel/finish by resolving `false` gets the rows deleted in the undo window back under Undo.
    onDiscard: () => void | Promise<boolean>;
    // Publish was accepted (202) for this many selected items; the page now polls the batch.
    onPublished: (count: number) => void;
    // Publish was refused or failed; the page decides what the error means (a 400 is "nothing left to add").
    onPublishError: (error: unknown) => void;
    // Called once the admin has confirmed "Finish import" (PARTIALLY_PUBLISHED only).
    onFinish: () => void | Promise<boolean>;
    // Retry the failed commit (PARTIALLY_COMMITTED only).
    onRetry: () => void;
};

type _ReviewDialog = { message: _ImportMessage; onConfirm?: () => void };

const languageTexts = {
    SEARCH: MENU_IMPORT_REVIEW_SEARCH,
    SELECT_ITEM: MENU_IMPORT_REVIEW_SELECT_ITEM,
    SELECT_ALL: MENU_IMPORT_REVIEW_SELECT_ALL,
    CLEAR: MENU_IMPORT_REVIEW_CLEAR,
    CLEAR_ARIA: MENU_IMPORT_REVIEW_CLEAR_ARIA,
    REMOVE_ARIA: MENU_IMPORT_REVIEW_REMOVE_ARIA,
    MARK_READY_ARIA: MENU_IMPORT_REVIEW_MARK_READY_ARIA,
    BACK_TO_REVIEW_ARIA: MENU_IMPORT_REVIEW_BACK_TO_REVIEW_ARIA,
    EDIT_ARIA: MENU_IMPORT_REVIEW_EDIT_ARIA,
    DELETE_ARIA: MENU_IMPORT_REVIEW_DELETE_ARIA,
    SELECTED_COUNT: MENU_IMPORT_REVIEW_SELECTED_COUNT,
    SELECT_ALL_READY: MENU_IMPORT_REVIEW_SELECT_ALL_READY,
    REMOVE_COUNT: MENU_IMPORT_REVIEW_REMOVE_COUNT,
    BULK_ACTIONS_ARIA: MENU_IMPORT_REVIEW_BULK_ACTIONS_ARIA,
    FILTER_ARIA: MENU_IMPORT_REVIEW_FILTER_ARIA,
    ITEM_DELETED: MENU_IMPORT_REVIEW_ITEM_DELETED,
    ITEMS_DELETED: MENU_IMPORT_REVIEW_ITEMS_DELETED,
    SHOWING: MENU_IMPORT_REVIEW_SHOWING,
    FOOTER_HINT_PENDING: MENU_IMPORT_REVIEW_FOOTER_HINT_PENDING,
    ADD_N_ITEM: MENU_IMPORT_ADD_N_ITEM,
    ADD_N_ITEMS: MENU_IMPORT_ADD_N_ITEMS,
    ADD_TO_MENU: MENU_IMPORT_ADD_TO_MENU_LOWER,
    SAVE_ITEM: MENU_IMPORT_REVIEW_SAVE_ITEM,
    CANCEL_EDIT: MENU_IMPORT_REVIEW_CANCEL_EDIT,
    COL_NAME: MENU_IMPORT_REVIEW_COL_NAME,
    COL_DESCRIPTION: MENU_IMPORT_REVIEW_COL_DESCRIPTION,
    COL_PRICE: MENU_IMPORT_REVIEW_COL_PRICE,
    FILES: MENU_IMPORT_REVIEW_FILES,
};

const TABS: { id: _ReviewTab; label: string }[] = [
    { id: 'all', label: MENU_IMPORT_REVIEW_TAB_ALL },
    { id: 'ready', label: MENU_IMPORT_REVIEW_READY },
    { id: 'needsReview', label: MENU_IMPORT_REVIEW_NEEDS_REVIEW },
    { id: 'added', label: MENU_IMPORT_ADDED },
];

// The icon of the source-files button: every file a PDF, every file an image, else the generic "several / mixed" one.
const sourceIcon = (files: { fileName: string; mimeType?: string }[]) => {
    const kinds = new Set(files.map((file) => getImportFileKind(file.fileName, file.mimeType)));
    if (files.length === 1 || kinds.size === 1) {
        if (kinds.has('pdf')) return 'ri-file-pdf-2-line';
        if (kinds.has('image')) return 'ri-image-line';
    }
    return 'ri-file-copy-2-line';
};

const REFETCH_KINDS = ['conflict', 'invalidNodeId', 'committedNodeLocked', 'validation', 'notFound'];

const FIELD_ERRORS: Record<keyof _ReviewItemForm, string> = {
    itemName: MENU_IMPORT_REVIEW_ERROR_NAME,
    itemDescription: MENU_IMPORT_REVIEW_ERROR_DESCRIPTION,
    defaultPrice: MENU_IMPORT_REVIEW_ERROR_PRICE,
};

/** The "Review Extracted Menu" table: every change is saved to the server draft straight away. */
const ReviewStep = ({
    venueId,
    batch,
    busy = false,
    onDiscard,
    onPublished,
    onPublishError,
    onFinish,
    onRetry,
}: Readonly<_ReviewStepProps>) => {
    const queryClient = useQueryClient();
    const translated = useTranslation(languageTexts, 'none');
    const text = (key: keyof typeof languageTexts) => translated[key] ?? languageTexts[key];

    const [tab, setTab] = useState<_ReviewTab>('all');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const [editingId, setEditingId] = useState<string | null>(null);
    // The table is controlled so a new tab or search can send it back to its first page.
    const [first, setFirst] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [saving, setSaving] = useState(false);
    // State updates land after the click, so a ref is what stops a second save starting while one is in flight.
    const savingRef = useRef(false);
    // The undo window's background save is running: the controls that save are off, nothing else changes (no block overlay).
    const [expiring, setExpiring] = useState(false);
    const [dialog, setDialog] = useState<_ReviewDialog | null>(null);
    const [sourcesOpen, setSourcesOpen] = useState(false);

    const batchKey = [MENU_IMPORT_BATCH, venueId, batch.id];
    const sourceFiles = batch.sourceFiles ?? [];
    const tree = batch.draftTree;
    // The undo window ended: the removal is saved in the background. It leaves the selection, any open edit and the page's
    // interaction alone (no `saving`, so no block overlay); `savingRef` still stops a second save starting beside it.
    const expireRemoval = async (ids: string[]) => {
        if (!tree) {
            pending.release(ids);
            return;
        }
        // Another save is in flight: keep the rows pending and try again when the next window closes, never drop them.
        if (savingRef.current) {
            pending.requeue(ids);
            return;
        }
        savingRef.current = true;
        setExpiring(true);
        try {
            const sent = removeItems(tree, ids);
            const response = await updateDraft(queryClient, venueId, batch.id, batch.draftVersion, sent);
            const { draftVersion } = response.data.data;
            const draftTree = keepServerFields(sent, response.data.data.draftTree);
            queryClient.setQueryData<_MenuImportBatchResponse | undefined>(batchKey, (previous) =>
                previous ? { ...previous, data: { ...previous.data, data: { ...previous.data.data, draftTree, draftVersion } } } : previous
            );
            // Only the deleted rows leave the selection: whatever else is ticked stays ticked.
            const remaining = new Set(flattenDraft(draftTree).map((row) => row.nodeId));
            setSelected((previous) =>
                Array.from(previous).every((id) => remaining.has(id))
                    ? previous
                    : new Set(Array.from(previous).filter((id) => remaining.has(id)))
            );
        } catch (error) {
            showSaveError(error);
        } finally {
            savingRef.current = false;
            setExpiring(false);
            // Success: the saved tree no longer has the rows. Failure: they are back in the table.
            pending.release(ids);
        }
    };
    // The step unmounted inside the window: send the removal best-effort; the page is gone, so there is nobody to tell.
    const flushRemoval = async (ids: string[]) => {
        if (!tree) return;
        try {
            await updateDraft(queryClient, venueId, batch.id, batch.draftVersion, removeItems(tree, ids));
        } catch (error) {
            logger.error('Bulk upload: could not save a pending removal', error);
        } finally {
            // The cached batch still has the removed rows; the next mount must read the saved draft.
            queryClient.invalidateQueries(batchKey);
        }
    };
    const pending = usePendingRemoval({ onExpire: expireRemoval, onFlush: flushRemoval });
    const allRows = useMemo(() => flattenDraft(tree), [tree]);
    // Rows deleted inside the undo window, or whose removal is still being saved, are hidden everywhere (table, counts, selection).
    const rows = useMemo(
        () => (pending.hiddenIds.size > 0 ? allRows.filter((row) => !pending.hiddenIds.has(row.nodeId)) : allRows),
        [allRows, pending.hiddenIds]
    );
    // A confirm dialog acts on the draft it was opened against, so no save may land under it: the undo window waits.
    const dialogOpen = dialog !== null;
    const { pause: pausePending, resume: resumePending } = pending;
    useEffect(() => {
        if (dialogOpen) pausePending();
        else resumePending();
    }, [dialogOpen, pausePending, resumePending]);
    const visibleRows = useMemo(() => filterReviewRows(rows, tab, search), [rows, tab, search]);
    // The paginator lives in the footer, so the table is only given the current page.
    // A delete or a filter can leave `first` past the last row; clamp it while rendering so no empty page is drawn.
    const safeFirst =
        first > 0 && first >= visibleRows.length ? Math.max(0, Math.floor((visibleRows.length - 1) / pageSize) * pageSize) : first;
    const pageRows = useMemo(() => visibleRows.slice(safeFirst, safeFirst + pageSize), [visibleRows, safeFirst, pageSize]);
    const sourceLabel = sourceFiles.length === 1 ? sourceFiles[0].fileName : `${sourceFiles.length} ${text('FILES')}`;
    const needsReviewCount = rows.filter((row) => row.needsReview).length;
    const counts: Record<_ReviewTab, number> = {
        all: rows.length,
        needsReview: needsReviewCount,
        ready: rows.length - needsReviewCount,
        added: rows.filter((row) => row.committed).length,
    };
    // The server is adding items (APPROVED/COMMITTING), or a commit failed and only Retry is left: nothing here may change.
    const adding = batch.status === 'COMMITTING' || batch.status === 'APPROVED';
    const commitFailed = batch.status === 'PARTIALLY_COMMITTED';
    const locked = saving || busy || adding || commitFailed;
    // Controls that start a save also wait for the undo window's background save (a second save would be ignored silently).
    const saveLocked = locked || expiring;
    // Select-all covers every selectable row of the current filter and search result, on every page.
    const selectableIds = useMemo(
        () => visibleRows.filter((row) => !row.needsReview && !row.committed).map((row) => row.nodeId),
        [visibleRows]
    );
    const selectedSelectable = selectableIds.filter((id) => selected.has(id)).length;
    const allSelected = selectableIds.length > 0 && selectedSelectable === selectableIds.length;
    const someSelected = selectedSelectable > 0 && !allSelected;
    const selectAllRef = useRef<HTMLInputElement>(null);
    // No deps list: `indeterminate` is a DOM property React never sets, so it is re-applied after every render and a remount cannot leave it stale.
    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
    });
    const visibleTabs = TABS.filter(({ id }) => id !== 'added' || counts.added > 0);

    const reload = () => {
        setEditingId(null);
        setSelected(new Set());
        queryClient.invalidateQueries(batchKey);
    };

    const showSaveError = (error: unknown) => {
        const { kind } = classifyMenuImportError(error);
        if (kind === 'conflict') {
            setDialog({
                message: { variant: 'warning', title: MENU_IMPORT_REVIEW_CONFLICT_TITLE, message: MENU_IMPORT_REVIEW_CONFLICT_MSG },
            });
        } else if (REFETCH_KINDS.includes(kind)) {
            setDialog({ message: { variant: 'error', title: MENU_IMPORT_ERROR_TITLE, message: MENU_IMPORT_REVIEW_SAVE_REFUSED } });
        } else {
            setDialog({
                message: {
                    variant: 'error',
                    title: MENU_IMPORT_ERROR_TITLE,
                    message: kind === 'network' || kind === 'unavailable' ? MENU_IMPORT_ERROR_UNAVAILABLE : MENU_IMPORT_ERROR_GENERIC,
                },
            });
        }
        if (REFETCH_KINDS.includes(kind)) reload();
    };

    // Rows deleted inside the undo window are folded into this save, so one call writes everything.
    const save = async (nextTree: _DraftTree): Promise<boolean> => {
        if (savingRef.current) return false;
        savingRef.current = true;
        setSaving(true);
        const removed = pending.take();
        try {
            const sent = removed.length > 0 ? removeItems(nextTree, removed) : nextTree;
            const response = await updateDraft(queryClient, venueId, batch.id, batch.draftVersion, sent);
            const { draftVersion } = response.data.data;
            const draftTree = keepServerFields(sent, response.data.data.draftTree);
            queryClient.setQueryData<_MenuImportBatchResponse | undefined>(batchKey, (previous) =>
                previous ? { ...previous, data: { ...previous.data, data: { ...previous.data.data, draftTree, draftVersion } } } : previous
            );
            setSelected(new Set());
            setEditingId(null);
            return true;
        } catch (error) {
            showSaveError(error);
            return false;
        } finally {
            savingRef.current = false;
            setSaving(false);
            // Saved out of the tree, or (on failure) back in the table.
            pending.release(removed);
        }
    };
    // Confirm dialogs run later than the render that opened them, so they act on the latest tree, draft version and save.
    const latest = useRef({ tree, save });
    latest.current = { tree, save };

    // The server adds what the saved draft marks "selected", so the checked rows are saved first and only then published.
    const addToMenu = async () => {
        if (!tree || selected.size === 0 || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        const count = selected.size;
        // Rows deleted inside the undo window are removed in this save, so they never reach the publish.
        const taken = pending.take();
        const liveTree = taken.length > 0 ? removeItems(tree, taken) : tree;
        try {
            try {
                const sent = applySelection(liveTree, selected);
                const response = await updateDraft(queryClient, venueId, batch.id, batch.draftVersion, sent);
                const { draftVersion } = response.data.data;
                const draftTree = keepServerFields(sent, response.data.data.draftTree);
                queryClient.setQueryData<_MenuImportBatchResponse | undefined>(batchKey, (previous) =>
                    previous
                        ? { ...previous, data: { ...previous.data, data: { ...previous.data.data, draftTree, draftVersion } } }
                        : previous
                );
            } catch (error) {
                showSaveError(error);
                return;
            }
            try {
                await publishBatch(queryClient, venueId, batch.id);
            } catch (error) {
                onPublishError(error);
                return;
            }
            queryClient.setQueryData<_MenuImportBatchResponse | undefined>(batchKey, (previous) =>
                previous ? { ...previous, data: { ...previous.data, data: { ...previous.data.data, status: 'COMMITTING' } } } : previous
            );
            setSelected(new Set());
            onPublished(count);
        } finally {
            savingRef.current = false;
            setSaving(false);
            pending.release(taken);
        }
    };

    const editingRow = rows.find((row) => row.nodeId === editingId);
    const formik = useFormik<_ReviewItemForm>({
        initialValues: editingRow ? convertRowToReviewItemForm(editingRow) : initialReviewItemForm,
        enableReinitialize: true,
        validationSchema: reviewItemSchema,
        onSubmit: async (values) => {
            if (!tree || !editingId) return;
            await save(editItem(tree, editingId, { ...values, itemName: values.itemName.trim() }));
        },
    });

    const confirmRow = (row: _ReviewRow) => tree && save(confirmItems(tree, [row.nodeId]));

    const askRemove = (ids: string[]) =>
        setDialog({
            message: {
                variant: 'warning',
                title: MENU_IMPORT_REVIEW_REMOVE_TITLE,
                message: ids.length === 1 ? MENU_IMPORT_REVIEW_REMOVE_ONE : MENU_IMPORT_REVIEW_REMOVE_MANY,
            },
            onConfirm: () => {
                const { tree: current, save: saveNow } = latest.current;
                if (current) saveNow(removeItems(current, ids));
            },
        });

    // Discard / Finish drop the deleted rows with the batch, but only once that succeeded: a refused call leaves the review open, so
    // the rows come back under Undo (still pending) instead of staying hidden while the tree still has them.
    const leave = async (end: () => void | Promise<boolean>) => {
        const taken = pending.take();
        let done = true;
        try {
            done = (await end()) !== false;
        } catch {
            done = false;
        }
        if (!done) pending.requeue(taken);
    };

    const askDiscard = () =>
        setDialog({
            message: {
                variant: 'warning',
                title: MENU_IMPORT_REVIEW_DISCARD_TITLE,
                message: batch.status === 'PARTIALLY_PUBLISHED' ? MENU_IMPORT_DISCARD_ADDED_MSG : MENU_IMPORT_REVIEW_DISCARD_MSG,
            },
            onConfirm: () => leave(onDiscard),
        });

    const askFinish = () =>
        setDialog({
            message: { variant: 'warning', title: MENU_IMPORT_FINISH_TITLE, message: MENU_IMPORT_FINISH_MSG },
            onConfirm: () => leave(onFinish),
        });

    const closeDialog = () => setDialog(null);
    const confirmDialog = () => {
        const onConfirm = dialog?.onConfirm;
        setDialog(null);
        onConfirm?.();
    };

    const toggleSelected = (nodeId: string) =>
        setSelected((previous) => {
            const next = new Set(previous);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });

    const selectReady = () => setSelected((previous) => new Set([...Array.from(previous), ...selectableIds]));
    const clearReady = () =>
        setSelected((previous) => {
            const next = new Set(previous);
            selectableIds.forEach((id) => next.delete(id));
            return next;
        });
    // The header checkbox clears when any Ready row is selected (checked or indeterminate), else selects them all.
    const toggleSelectAll = selectedSelectable > 0 ? clearReady : selectReady;

    const startEdit = (row: _ReviewRow) => {
        formik.resetForm({ values: convertRowToReviewItemForm(row) });
        setEditingId(row.nodeId);
    };

    const backToReview = (row: _ReviewRow) => tree && save(markForReview(tree, [row.nodeId]));

    // Deleting is instant and undoable: the row is only hidden here, and the draft is saved when the undo window closes.
    const deleteRow = (row: _ReviewRow) => {
        pending.remove(row.nodeId, row.itemName);
        setSelected((previous) => {
            if (!previous.has(row.nodeId)) return previous;
            const next = new Set(previous);
            next.delete(row.nodeId);
            return next;
        });
        if (editingId === row.nodeId) setEditingId(null);
    };

    const fieldError = (field: keyof _ReviewItemForm) =>
        formik.touched[field] && formik.errors[field] ? (
            <span className="bulk-upload-error-text" role="alert">
                <JTranslation text={FIELD_ERRORS[field]} />
            </span>
        ) : null;

    const renderField = (field: keyof _ReviewItemForm, label: string) => (
        <div className="bulk-upload-review-edit">
            <InputText
                name={field}
                aria-label={label}
                value={formik.values[field]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={locked}
            />
            {fieldError(field)}
        </div>
    );

    const renderText = (row: _ReviewRow, field: 'itemName' | 'itemDescription' | 'defaultPrice', label: string) =>
        row.nodeId === editingId ? renderField(field, label) : row[field];

    const iconButton = (
        icon: string,
        ariaKey: keyof typeof languageTexts,
        row: _ReviewRow,
        onClick: () => void,
        disabled: boolean = locked
    ) => (
        <MlxButton
            text
            rounded
            icon={icon}
            aria-label={text(ariaKey).replace('{name}', row.itemName)}
            disabled={disabled}
            onClick={onClick}
        />
    );

    const renderActions = (row: _ReviewRow) => {
        if (row.nodeId === editingId) {
            return (
                <div className="bulk-upload-review-actions">
                    <MlxButton
                        text
                        rounded
                        icon="ri-close-line"
                        aria-label={text('CANCEL_EDIT')}
                        disabled={locked}
                        onClick={() => setEditingId(null)}
                    />
                    <MlxButton
                        text
                        rounded
                        icon="ri-check-double-line"
                        aria-label={text('SAVE_ITEM')}
                        disabled={saveLocked}
                        onClick={() => formik.submitForm()}
                    />
                </div>
            );
        }
        if (row.committed) return null;
        return (
            <div className="bulk-upload-review-actions">
                {row.needsReview ? (
                    <MlxButton
                        outlined
                        className="bulk-upload-mark-ready"
                        aria-label={text('MARK_READY_ARIA').replace('{name}', row.itemName)}
                        disabled={saveLocked}
                        onClick={() => confirmRow(row)}
                    >
                        <JTranslation text={MENU_IMPORT_REVIEW_MARK_READY} />
                    </MlxButton>
                ) : (
                    iconButton('ri-arrow-go-back-line', 'BACK_TO_REVIEW_ARIA', row, () => backToReview(row), saveLocked)
                )}
                {iconButton('ri-edit-line', 'EDIT_ARIA', row, () => startEdit(row))}
                {iconButton('ri-delete-bin-line', 'DELETE_ARIA', row, () => deleteRow(row))}
            </div>
        );
    };

    const renderStatus = (row: _ReviewRow) => {
        if (row.committed) return <MlxBadge value={MENU_IMPORT_ADDED} severity="info" />;
        return (
            <MlxBadge
                value={row.needsReview ? MENU_IMPORT_REVIEW_NEEDS_REVIEW : MENU_IMPORT_REVIEW_READY}
                severity={row.needsReview ? 'warning' : 'success'}
            />
        );
    };

    return (
        <MlxBlockUI isLoading={saving} blocked={adding} opacity={0.3}>
            {adding && (
                <div className="bulk-upload-adding" role="status" data-testid="review-adding">
                    <i className="ri-loader-4-line rotating-icon text-primary" />
                    <span className="bulk-upload-title">
                        <JTranslation text={MENU_IMPORT_ADDING_TITLE} />
                    </span>
                    <span className="bulk-upload-muted">
                        <JTranslation text={MENU_IMPORT_ADDING_DESC} />
                    </span>
                </div>
            )}
            <div className="bulk-upload-card bulk-upload-review" data-testid="review-step">
                {selected.size === 0 ? (
                    <div className="bulk-upload-review-toolbar">
                        <div className="bulk-upload-review-tabs" role="tablist" aria-label={text('FILTER_ARIA')}>
                            {visibleTabs.map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    role="tab"
                                    data-testid={`review-tab-${id}`}
                                    aria-selected={tab === id}
                                    className={tab === id ? 'selected' : ''}
                                    onClick={() => {
                                        setTab(id);
                                        setFirst(0);
                                        setEditingId(null);
                                    }}
                                >
                                    <JTranslation text={label} /> ({counts[id]})
                                </button>
                            ))}
                        </div>
                        <div className="bulk-upload-review-toolbar-side">
                            <MlxSearch
                                value={search}
                                placeholder={text('SEARCH')}
                                aria-label={text('SEARCH')}
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                    setFirst(0);
                                    setEditingId(null);
                                }}
                            />
                            {sourceFiles.length > 0 && (
                                <MlxButton
                                    text
                                    type="button"
                                    className="bulk-upload-source-button"
                                    data-testid="review-source"
                                    aria-label={sourceLabel}
                                    title={sourceFiles.map((file) => file.fileName).join(', ')}
                                    onClick={() => setSourcesOpen(true)}
                                >
                                    <i className={sourceIcon(sourceFiles)} />
                                    {sourceFiles.length > 1 && (
                                        <span className="bulk-upload-source-badge" data-testid="review-source-count">
                                            {sourceFiles.length}
                                        </span>
                                    )}
                                </MlxButton>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bulk-upload-bulk-bar" role="group" aria-label={text('BULK_ACTIONS_ARIA')} data-testid="review-bulk-bar">
                        <span className="bulk-upload-bulk-count">{text('SELECTED_COUNT').replace('{count}', String(selected.size))}</span>
                        {selectableIds.length > selectedSelectable && (
                            <MlxButton text type="button" disabled={locked} onClick={selectReady}>
                                {text('SELECT_ALL_READY').replace('{count}', String(selectableIds.length))}
                            </MlxButton>
                        )}
                        <MlxButton
                            text
                            type="button"
                            aria-label={text('CLEAR_ARIA')}
                            disabled={locked}
                            onClick={() => setSelected(new Set())}
                        >
                            {text('CLEAR')}
                        </MlxButton>
                        <MlxButton
                            outlined
                            className="bulk-upload-remove-selected"
                            aria-label={text('REMOVE_ARIA').replace('{count}', String(selected.size))}
                            disabled={saveLocked}
                            onClick={() => askRemove(Array.from(selected))}
                        >
                            <i className="ri-delete-bin-line" />
                            <span data-testid="review-selected-count">
                                {text('REMOVE_COUNT').replace('{count}', String(selected.size))}
                            </span>
                        </MlxButton>
                    </div>
                )}

                {pending.pendingIds.size > 0 && (
                    <div className="bulk-upload-undo-bar" role="status" data-testid="review-undo-bar">
                        <span>
                            {pending.pendingIds.size === 1
                                ? text('ITEM_DELETED').replace('{name}', pending.lastName ?? '')
                                : text('ITEMS_DELETED').replace('{count}', String(pending.pendingIds.size))}
                        </span>
                        <MlxButton text type="button" data-testid="review-undo" onClick={pending.undo}>
                            <JTranslation text={MENU_IMPORT_REVIEW_UNDO} />
                        </MlxButton>
                    </div>
                )}

                <DataTable
                    value={pageRows}
                    dataKey="nodeId"
                    className="custom-p-table bulk-upload-review-table"
                    scrollable
                    scrollHeight="flex"
                    responsiveLayout="stack"
                    // Keep in step with the media queries in BulkUpload.scss: stacked up to 960px, columns from 960.02px (no gap between them).
                    breakpoint="960px"
                    rowClassName={(row: _ReviewRow) => ({
                        'bulk-upload-review-row-flagged': row.needsReview,
                        'bulk-upload-review-row-editing': row.nodeId === editingId,
                    })}
                    // NOTE: this header group repeats the header text of the columns below; a new column must be added to both.
                    // The select-all lives in an explicit header group: a `header` on the select column would be repeated in every
                    // row as its stacked label.
                    headerColumnGroup={
                        <ColumnGroup>
                            <Row>
                                <Column
                                    className="bulk-upload-col-select"
                                    header={
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            aria-label={text('SELECT_ALL')}
                                            checked={allSelected}
                                            disabled={locked || selectableIds.length === 0}
                                            onChange={toggleSelectAll}
                                        />
                                    }
                                />
                                <Column className="bulk-upload-col-name" header={<JTranslation text={MENU_IMPORT_REVIEW_COL_NAME} />} />
                                <Column
                                    className="bulk-upload-col-description"
                                    header={<JTranslation text={MENU_IMPORT_REVIEW_COL_DESCRIPTION} />}
                                />
                                <Column className="bulk-upload-col-price" header={<JTranslation text={MENU_IMPORT_REVIEW_COL_PRICE} />} />
                                <Column className="bulk-upload-col-status" header={<JTranslation text={STATUS} />} />
                                <Column className="bulk-upload-col-actions" />
                            </Row>
                        </ColumnGroup>
                    }
                    emptyMessage={<JTranslation text={rows.length === 0 ? MENU_IMPORT_REVIEW_EMPTY : MENU_IMPORT_REVIEW_NO_MATCH} />}
                >
                    <Column
                        className="bulk-upload-col-select"
                        body={(row: _ReviewRow) => (
                            <input
                                type="checkbox"
                                aria-label={text('SELECT_ITEM')}
                                checked={selected.has(row.nodeId)}
                                disabled={locked || row.needsReview || row.committed}
                                onChange={() => toggleSelected(row.nodeId)}
                            />
                        )}
                    />
                    <Column
                        className="bulk-upload-col-name"
                        body={(row: _ReviewRow) => (
                            <div className="bulk-upload-item">
                                {renderText(row, 'itemName', text('COL_NAME'))}
                                <span className="bulk-upload-item-category">{row.categoryName}</span>
                            </div>
                        )}
                    />
                    <Column
                        className="bulk-upload-col-description"
                        body={(row: _ReviewRow) =>
                            row.nodeId === editingId ? (
                                renderField('itemDescription', text('COL_DESCRIPTION'))
                            ) : row.itemDescription ? (
                                <span className="bulk-upload-description" title={row.itemDescription}>
                                    {row.itemDescription}
                                </span>
                            ) : (
                                <span className="bulk-upload-muted">
                                    <JTranslation text={MENU_IMPORT_REVIEW_NO_DESCRIPTION} />
                                </span>
                            )
                        }
                    />
                    <Column
                        className="bulk-upload-col-price"
                        body={(row: _ReviewRow) => renderText(row, 'defaultPrice', text('COL_PRICE'))}
                    />
                    <Column className="bulk-upload-col-status" body={renderStatus} />
                    <Column className="bulk-upload-col-actions" body={renderActions} />
                </DataTable>

                <div className="bulk-upload-review-pagination">
                    <span className="bulk-upload-muted" data-testid="review-showing">
                        {visibleRows.length === 0 ? (
                            <JTranslation text={MENU_IMPORT_REVIEW_SHOWING_NONE} />
                        ) : (
                            text('SHOWING')
                                .replace('{from}', String(safeFirst + 1))
                                .replace('{to}', String(Math.min(safeFirst + pageSize, visibleRows.length)))
                                .replace('{total}', String(visibleRows.length))
                        )}
                    </span>
                    <Paginator
                        className="bulk-upload-review-paginator"
                        first={safeFirst}
                        rows={pageSize}
                        totalRecords={visibleRows.length}
                        rowsPerPageOptions={[10, 25, 50]}
                        template={defaultPaginationEvent.paginatorTemplate as string}
                        onPageChange={(event) => {
                            setFirst(event.first);
                            setPageSize(event.rows);
                            setEditingId(null);
                        }}
                    />
                </div>

                <div className="bulk-upload-review-footer">
                    <div className="bulk-upload-review-footer-main">
                        <MlxButton outlined disabled={saveLocked} onClick={askDiscard}>
                            <JTranslation text={MENU_IMPORT_REVIEW_DISCARD} />
                        </MlxButton>
                        {batch.status === 'PARTIALLY_PUBLISHED' && (
                            <MlxButton outlined disabled={saveLocked} onClick={askFinish}>
                                <JTranslation text={MENU_IMPORT_FINISH} />
                            </MlxButton>
                        )}
                        {commitFailed && (
                            <MlxButton disabled={busy} onClick={onRetry}>
                                <JTranslation text={MENU_IMPORT_RETRY} />
                            </MlxButton>
                        )}
                    </div>
                    {!commitFailed && (
                        <div className="bulk-upload-review-footer-actions">
                            <span className="bulk-upload-muted bulk-upload-review-hint" data-testid="review-hint">
                                <JTranslation text={MENU_IMPORT_REVIEW_FOOTER_HINT} />
                                {counts.needsReview > 0 && (
                                    <> · {text('FOOTER_HINT_PENDING').replace('{count}', String(counts.needsReview))}</>
                                )}
                            </span>
                            <MlxButton disabled={saveLocked || selected.size === 0} onClick={addToMenu}>
                                {selected.size === 0
                                    ? text('ADD_TO_MENU')
                                    : text(selected.size === 1 ? 'ADD_N_ITEM' : 'ADD_N_ITEMS').replace('{count}', String(selected.size))}
                            </MlxButton>
                        </div>
                    )}
                </div>
            </div>
            <MlxDialog
                visible={sourcesOpen}
                onHide={() => setSourcesOpen(false)}
                className="mlx-dialog bulk-upload-source-dialog"
                // MlxDialog falls back to an inline min-width when no style is given; an empty one leaves the width to the class.
                style={{}}
                header={
                    <>
                        <JTranslation text={MENU_IMPORT_REVIEW_SOURCE_TITLE} /> ({sourceFiles.length})
                    </>
                }
                footer={
                    <div className="d-flex justify-content-end">
                        <MlxButton data-testid="review-source-close" onClick={() => setSourcesOpen(false)}>
                            <JTranslation text={MENU_IMPORT_CLOSE} />
                        </MlxButton>
                    </div>
                }
            >
                <ul className="bulk-upload-source-list" data-testid="review-source-list">
                    {sourceFiles.map((file, index) => (
                        <li key={`${file.fileName}-${index}`}>
                            <span className="bulk-upload-source-name">{file.fileName}</span>
                            {file.sizeBytes !== undefined && <span className="bulk-upload-muted">{formatFileSize(file.sizeBytes)}</span>}
                        </li>
                    ))}
                </ul>
            </MlxDialog>
            <ImportMessageDialog
                message={dialog?.message ?? null}
                onPrimary={dialog?.message.variant === 'warning' && dialog.onConfirm ? confirmDialog : closeDialog}
                onSecondary={closeDialog}
            />
        </MlxBlockUI>
    );
};

export default ReviewStep;
